import React, { useState } from "react";
import { Mail, Check, Loader2 } from "lucide-react";
import { api } from "../lib/api";
import { useI18n } from "../contexts/I18nContext";

export default function NewsletterSignup({ compact = false }) {
  const { lang } = useI18n();
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [status, setStatus] = useState("idle"); // idle | loading | success | error | exists
  const [errMsg, setErrMsg] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    if (!email.includes("@")) {
      setErrMsg(lang === "en" ? "Please enter a valid email" : "Introdu un email valid");
      setStatus("error");
      return;
    }
    setStatus("loading");
    setErrMsg("");
    try {
      const r = await api.post("/newsletter/signup", { email, name: name || null, source: "footer", lang });
      if (r.data?.status === "already_subscribed") {
        setStatus("exists");
      } else {
        setStatus("success");
      }
    } catch (e) {
      setErrMsg(e?.response?.data?.detail || (lang === "en" ? "Something went wrong" : "A aparut o eroare"));
      setStatus("error");
    }
  };

  if (status === "success" || status === "exists") {
    return (
      <div className="flex items-center gap-2 text-sm" style={{ color: "var(--brand-primary)" }} data-testid="newsletter-success">
        <Check className="w-4 h-4" />
        {status === "exists"
          ? (lang === "en" ? "You're already subscribed — thank you!" : "Esti deja abonat — multumim!")
          : (lang === "en" ? "Thanks for subscribing!" : "Multumim pentru abonare!")}
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="space-y-2" data-testid="newsletter-form">
      <div className="flex items-center gap-2 mb-1 text-xs" style={{ color: "var(--brand-text-secondary)" }}>
        <Mail className="w-3.5 h-3.5" />
        {lang === "en" ? "Get fleet updates & exclusive offers" : "Primeste noutati si oferte exclusive"}
      </div>
      {!compact && (
        <input
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder={lang === "en" ? "Your name (optional)" : "Numele tau (optional)"}
          className="form-input w-full text-sm"
          data-testid="newsletter-name"
        />
      )}
      <div className={compact ? "flex gap-1.5" : "flex gap-2"}>
        <input
          type="email"
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="you@example.com"
          className={compact ? "form-input flex-1 text-xs px-2.5 py-1.5" : "form-input flex-1 text-sm"}
          data-testid="newsletter-email"
        />
        <button
          type="submit"
          disabled={status === "loading"}
          className={
            compact
              ? "btn-gold-filled text-xs px-2.5 py-1.5 inline-flex items-center justify-center"
              : "btn-gold-filled text-sm px-4 py-2 inline-flex items-center gap-1"
          }
          data-testid="newsletter-submit"
          aria-label={lang === "en" ? "Subscribe" : "Aboneaza-te"}
        >
          {status === "loading" ? (
            <Loader2 className="w-3.5 h-3.5 animate-spin" />
          ) : compact ? (
            <Mail className="w-3.5 h-3.5" />
          ) : (
            <>
              <Mail className="w-3.5 h-3.5" />
              {lang === "en" ? "Subscribe" : "Aboneaza-te"}
            </>
          )}
        </button>
      </div>
      {status === "error" && (
        <div className="text-xs" style={{ color: "#f87171" }} data-testid="newsletter-error">{errMsg}</div>
      )}
      <p className="text-[10px] leading-relaxed" style={{ color: "var(--brand-text-secondary)" }}>
        {lang === "en"
          ? "No spam. Unsubscribe anytime. We respect your privacy."
          : "Fara spam. Te poti dezabona oricand. Iti respectam confidentialitatea."}
      </p>
    </form>
  );
}
